import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Search, X, CalendarPlus, RefreshCw, Ban } from "lucide-react";
import { getAuthErrorMessage, loginInputClass } from "../LoginForm";
import { Avatar } from "../ProfileMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
const card = "rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100";
const fmtFt = (n) => `${new Intl.NumberFormat("hu-HU").format(n)} Ft`;
const fmtDate = (d) => (d ? d.replace(/-/g, ". ") + "." : "—");
const btn = (c) => `inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-semibold text-white disabled:opacity-60 ${c}`;

const STATE = {
  active: { label: "Aktív GLE-tagság", cls: "bg-emerald-100 text-emerald-800" },
  renewal_pending: { label: "Megújításra vár", cls: "bg-yellow-200 text-yellow-900" },
  none: { label: "Nincs GLE-tagság", cls: "bg-slate-200 text-slate-700" },
};

export function TagsagTab({ readOnly = false }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // { user, mode: "add" | "extend" }
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/ugyfelkapu/admin/memberships`, { params: { token: token() } })
    .then((r) => setData(r.data)).catch((e) => { toast.error(getAuthErrorMessage(e)); setData({ users: [], fee: 8000 }); });
  useEffect(() => { load(); }, []);

  const terminate = async (u) => {
    if (!window.confirm(`Biztosan megszünteted ${u.name} GLE-tagságát? A fiók és a korábbi befizetések megmaradnak.`)) return;
    setBusy(u.id);
    try { const r = await axios.post(`${API}/ugyfelkapu/admin/memberships/${u.id}/terminate`, {}, { params: { token: token() } }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(getAuthErrorMessage(e)); }
    setBusy("");
  };

  const needle = q.trim().toLowerCase();
  const users = (data?.users || []).filter((u) => !needle || [u.name, u.identifier].some((v) => (v || "").toLowerCase().includes(needle)));

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="tagsag-tab">
      <section className={card}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><BadgeCheck size={20} className="text-emerald-600" /> GLE-tagságok</h2>
          <p className="text-sm text-slate-700" data-testid="tagsag-fee-info">Tagsági díj: <span className="font-semibold">{fmtFt(data?.fee || 8000)}</span> / fő / naptári év · lejárat minden év december 31.</p>
        </div>
        <div className="relative mb-4">
          <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés név vagy azonosító alapján" className={`${loginInputClass} pl-11`} data-testid="tagsag-search-input" />
        </div>
        {!data ? <p className="py-8 text-center text-slate-600">Betöltés...</p> : users.length === 0 ? <p className="py-6 text-center text-sm text-slate-600" data-testid="tagsag-empty">Nincs találat.</p> : (
          <ul className="divide-y divide-slate-100" data-testid="tagsag-list">
            {users.map((u) => {
              const s = STATE[u.state]; const m = u.membership;
              return (
                <li key={u.id} className={`flex flex-col gap-3 px-2 py-3 sm:flex-row sm:items-center ${u.state === "renewal_pending" ? "bg-yellow-100 rounded-2xl" : ""}`} data-testid={`tagsag-row-${u.id}`} data-state={u.state}>
                  <Avatar user={u} className="h-10 w-10 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">{u.name}</span>
                      <span className="font-mono text-xs font-semibold tracking-wider text-slate-700">{u.identifier || "—"}</span>
                      {u.membership_code && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold tracking-widest text-slate-800" data-testid={`tagsag-code-${u.id}`}>Tagsági kód: {u.membership_code}</span>}
                    </div>
                    {m && <p className="text-xs text-slate-700" data-testid={`tagsag-details-${u.id}`}>{m.year}. év · fizetve: {fmtDate(m.paid_at)} · {fmtFt(m.amount)} · érvényes: {fmtDate(u.valid_until)}</p>}
                    {u.state === "renewal_pending" && <p className="text-xs font-medium text-yellow-900">Megújítható {fmtDate(u.renew_until)}-ig, utána automatikusan megszűnik.</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`} data-testid={`tagsag-state-${u.id}`}>{s.label}</span>
                    {!readOnly && u.state === "none" && <button type="button" onClick={() => setModal({ user: u, mode: "add" })} className={btn("bg-emerald-600 hover:bg-emerald-700")} data-testid={`tagsag-add-${u.id}`}><CalendarPlus size={15} /> Tagság hozzáadása</button>}
                    {!readOnly && u.state === "renewal_pending" && <>
                      <button type="button" onClick={() => setModal({ user: u, mode: "extend" })} className={btn("bg-emerald-600 hover:bg-emerald-700")} data-testid={`tagsag-extend-${u.id}`}><RefreshCw size={15} /> Tagság meghosszabbítása</button>
                      <button type="button" disabled={busy === u.id} onClick={() => terminate(u)} className={btn("bg-red-600 hover:bg-red-700")} data-testid={`tagsag-terminate-${u.id}`}><Ban size={15} /> Tagság megszüntetése</button>
                    </>}
                    {u.state === "active" && <span className="text-xs text-slate-600" data-testid={`tagsag-locked-${u.id}`}>Érvényes {fmtDate(u.valid_until)}-ig</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {modal && <PayModal {...modal} fee={data?.fee || 8000} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}

function PayModal({ user, mode, fee, onClose, onDone }) {
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const year = paidAt ? paidAt.slice(0, 4) : "";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ugyfelkapu/admin/memberships`, { user_id: user.id, paid_at: paidAt }, { params: { token: token() } });
      toast.success(r.data.message); onDone();
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="tagsag-modal">
      <form onSubmit={submit} className="w-full max-w-md space-y-4 rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{mode === "extend" ? "Tagság meghosszabbítása" : "Tagság hozzáadása"}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100" data-testid="tagsag-modal-close"><X size={22} /></button>
        </div>
        <p className="text-sm text-slate-800"><span className="font-semibold">{user.name}</span> <span className="font-mono text-xs text-slate-700">({user.identifier || "—"})</span></p>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Fizetés dátuma *</label>
          <input type="date" required value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className={loginInputClass} data-testid="tagsag-paid-at" />
        </div>
        <p className="rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-800" data-testid="tagsag-summary">Díj: <span className="font-semibold">{fmtFt(fee)}</span>{year && <> · A tagság a(z) <span className="font-semibold">{year}.</span> évre szól, lejárat: <span className="font-semibold">{year}. december 31.</span></>}</p>
        <button type="submit" disabled={busy} className="h-12 w-full rounded-xl bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="tagsag-save">{busy ? "Mentés..." : "Mentés"}</button>
      </form>
    </div>
  );
}

export default TagsagTab;
